import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lightbulb } from "lucide-react";

export default function Loader({ onComplete }) {
  const [displayText, setDisplayText] = useState('');
  const fullText = 'Specialisti in Illuminotecnica';
  const [charIndex, setCharIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  // Typewriter effect
  useEffect(() => {
    if (charIndex < fullText.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + fullText[charIndex]);
        setCharIndex(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [charIndex, fullText]);

  // Auto-complete after 3.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 800);
    }, 3500);

    return () => clearTimeout(timer);
  }, [onComplete]);

  // Animation variants for exit
  const containerVariants = {
    hidden: { 
      opacity: 1,
      scale: 1,
      y: 0,
      filter: 'blur(0)',
      backgroundColor: 'rgba(255, 255, 255, 1)'
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 20,
      filter: 'blur(10px)',
      backgroundColor: 'rgba(255, 255, 255, 0)',
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
        backgroundColor: {
          duration: 0.6,
          ease: 'easeInOut'
        },
        scale: { 
          duration: 0.8, 
          ease: [0.34, 1.56, 0.64, 1]
        },
        y: { 
          duration: 0.8, 
          ease: [0.22, 1, 0.36, 1] 
        },
        filter: { 
          duration: 0.6,
          ease: 'easeInOut'
        }
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <motion.div 
        className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 via-gray-50 to-[#F8F5E6] dark:from-gray-900 dark:via-gray-800 dark:to-[#1A1600]"
        variants={containerVariants}
        initial="hidden"
        animate={isExiting ? "exit" : "hidden"}
      >
        {/* Sfondo con pattern sottile */}
        <div className="absolute inset-0 opacity-10 dark:opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(45deg, #D4AF37 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }} />
        </div>

        {/* Main container */}
        <div className="relative z-10 flex flex-col items-center">
          {/* Animated arcs container */}
          <div className="relative w-40 h-40 flex items-center justify-center mb-8">
            {/* Outer arc */}
            <motion.div 
              className="absolute w-full h-full rounded-full border-4 border-transparent border-t-[#D4AF37]"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />
            
            {/* Inner arc */}
            <motion.div 
              className="absolute w-3/4 h-3/4 rounded-full border-4 border-transparent border-b-[#D4AF37]"
              animate={{ rotate: -360 }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
            />
            
            {/* Logo */}
            <div className="relative z-10">
              <img 
                src="./assets/EllegiLogoStartup.png" 
                alt="ELLEGI" 
                className="w-16 h-16 object-contain"
              />
            </div>
          </div>
          
          {/* Typewriter text */}
          <div className="text-center">
            <div className="text-2xl font-medium text-gray-800 dark:text-gray-200 h-10 flex items-center justify-center">
              {displayText}
              <span className="animate-pulse">|</span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
              Illuminiamo il tuo futuro dal 1986
            </p>
          </div>
        </div>

        {/* Effetto di luce */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-transparent pointer-events-none" 
          style={{
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255,215,0,0.05) 0%, transparent 60%)',
          }}
          animate={{
            opacity: isExiting ? 0 : 1,
            scale: isExiting ? 1.1 : 1,
          }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        />
      </motion.div>
    </div>
  );
}
